import { Input } from '@insify/design-system/components';
import { forwardRef, useReducer, useRef } from 'react';
import NumberFormat, { type NumberFormatValues } from 'react-number-format';

import { ProxyInput } from '../InputRadioGroup';

import type { InputProps } from '@insify/design-system/components';

type Props = InputProps & {
  isAllowed?: (values: NumberFormatValues) => boolean;
  decimalScale?: number;
  thousandSeparator?: string;
};

type ControlledProps = {
  value?: number;
  defaultValue?: number;
  onChange?: (value: number | undefined) => void;
  onUpdate?: (value: number | undefined) => void;
};

export type ControlledInputMoneyProps = Omit<Props, 'onChange'> & ControlledProps;

const InputMoney = ({
  decimalScale = 2,
  autoComplete = 'off',
  thousandSeparator = '.',
  ...props
}: ControlledInputMoneyProps) => {
  const floatValue = useRef<number | undefined>(undefined);

  return (
    <NumberFormat
      customInput={Input}
      {...props}
      type='text'
      onValueChange={(e) => {
        floatValue.current = e.floatValue;
        Boolean(props.onUpdate) && props.onUpdate?.(e.floatValue);
      }}
      onChange={() => {
        props.onChange?.(floatValue.current);
      }}
      allowedDecimalSeparators={['.', ',']}
      decimalScale={decimalScale}
      thousandSeparator={thousandSeparator}
      decimalSeparator={','}
      autoComplete={autoComplete}
    />
  );
};

const InputCents = forwardRef<HTMLInputElement, Props>(
  ({ decimalScale = 2, autoComplete = 'off', thousandSeparator = '.', ...props }, ref) => {
    const valueRef = useRef<string>();
    // https://reactjs.org/docs/hooks-faq.html#is-there-something-like-forceupdate
    const [_, forceUpdate] = useReducer((x) => x + 1, 0);

    return (
      <>
        <NumberFormat
          customInput={Input}
          {...props}
          type='text'
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          defaultValue={props.defaultValue as any}
          value={Number(valueRef.current) / 100}
          onValueChange={({ floatValue }) => {
            const hasValue = floatValue !== undefined;
            const hasEmptiedValue = valueRef.current !== undefined && !hasValue;
            if (hasValue || hasEmptiedValue) {
              valueRef.current = hasEmptiedValue ? '0' : `${floatValue}00`;
            }
          }}
          allowedDecimalSeparators={['.', ',']}
          decimalScale={decimalScale}
          // separators are hard-coded now but better to be configured, preferrable by the Intl API
          thousandSeparator={thousandSeparator}
          decimalSeparator={','}
          inputMode='numeric'
          autoComplete={autoComplete}
        />
        <ProxyInput
          value={valueRef}
          onValueChange={(value) => {
            if (valueRef.current !== value) {
              valueRef.current = value;
              // That valueRef is different from the value means that the value was updated by the reset method from useForm
              // To apply the new value, we have to re-render the component so that the value prop of NumberFormat is applied
              //
              // The value prop of NumberFormat works bit differently than the native input,
              // as it doesn't prevent user to update the field when the value is a fixed value (bit like defaultValue).
              // For such reason, having a state for the value prop and update the state here might sound a good idea
              // but re-rendering this component (e.g. by the error prop) happens before updating the state happens
              // which leads to that the component is rendered with the previous value briefly before being rendered with the new value
              // which makes the cursor jumps to an unintended place.
              forceUpdate();
            }
          }}
          ref={ref as React.RefCallback<HTMLInputElement>}
        />
      </>
    );
  },
);

export { InputMoney, InputCents };
